import { createEvent, restore, sample } from 'effector'
import { fx } from 'src/libs'
import {
  Participant,
  ParticipantListRequest,
  ParticipantListResponse,
} from './types'

export const participantsListMounted = createEvent<ParticipantListRequest>()

export const getParticipants = fx<
  ParticipantListRequest,
  ParticipantListResponse
>({
  method: 'GET',
  url: `/bonus150/participant`,
})

const MOCK_PARTICIPANTS: Participant[] = [
  {
    client: {
      clientId: 101,
      fullName: 'Alice Johnson',
      email: 'alice@example.com',
      isTest: false,
      isPremium: true,
    },
    status: 'active',
    bonusAccountId: 1001,
    withdrawalType: 'instant',
    sumBoostersLots: '12.50',
    sumBonusLots: '340.00',
    sumBonusProfit: '85.20',
    instantWithdrawalAmount: '50.00',
    coefficient: '1.5',
    withdrawalAmount: '127.80',
    sumWithdrawalLots: '200.00',
    sumCurrentWithdrawalLots: '140.00',
    isProfitWithdrawn: false,
    registeredTs: 1700000000,
    endedTs: null,
    bannedTs: null,
    banReason: null,
    modifiedTs: 1710000000,
    balance: 1250.75,
  },
  {
    client: {
      clientId: 102,
      fullName: 'Bob Smith',
      email: 'bob@example.com',
      isTest: true,
      isPremium: false,
    },
    status: 'active',
    bonusAccountId: 1002,
    withdrawalType: 'trading',
    sumBoostersLots: '8.00',
    sumBonusLots: '210.00',
    sumBonusProfit: '42.00',
    instantWithdrawalAmount: '0.00',
    coefficient: '1.2',
    withdrawalAmount: '50.40',
    sumWithdrawalLots: '100.00',
    sumCurrentWithdrawalLots: '80.00',
    isProfitWithdrawn: false,
    registeredTs: 1701000000,
    endedTs: null,
    bannedTs: null,
    banReason: null,
    modifiedTs: 1711000000,
    balance: 640.0,
  },
  {
    client: {
      clientId: 103,
      fullName: 'Carol White',
      email: 'carol@example.com',
      isTest: false,
      isPremium: true,
    },
    status: 'close',
    bonusAccountId: 1003,
    withdrawalType: 'instant',
    sumBoostersLots: '20.00',
    sumBonusLots: '500.00',
    sumBonusProfit: '150.00',
    instantWithdrawalAmount: '150.00',
    coefficient: '2.0',
    withdrawalAmount: '300.00',
    sumWithdrawalLots: '500.00',
    sumCurrentWithdrawalLots: '500.00',
    isProfitWithdrawn: true,
    registeredTs: 1702000000,
    endedTs: '2024-03-01T00:00:00Z',
    bannedTs: null,
    banReason: null,
    modifiedTs: 1712000000,
    balance: 0,
  },
  {
    client: {
      clientId: 104,
      fullName: 'David Lee',
      email: 'david@example.com',
      isTest: false,
      isPremium: false,
    },
    status: 'disqualified',
    bonusAccountId: 1004,
    withdrawalType: 'trading',
    sumBoostersLots: '3.00',
    sumBonusLots: '60.00',
    sumBonusProfit: '10.00',
    instantWithdrawalAmount: '0.00',
    coefficient: '1.0',
    withdrawalAmount: '10.00',
    sumWithdrawalLots: '30.00',
    sumCurrentWithdrawalLots: '10.00',
    isProfitWithdrawn: false,
    registeredTs: 1703000000,
    endedTs: null,
    bannedTs: '2024-04-15T00:00:00Z',
    banReason: 'Violated promotion terms',
    modifiedTs: 1713000000,
    balance: 60.0,
  },
  {
    client: {
      clientId: 105,
      fullName: 'Eva Martinez',
      email: 'eva@example.com',
      isTest: false,
      isPremium: true,
    },
    status: 'auto_banned',
    bonusAccountId: 1005,
    withdrawalType: 'instant',
    sumBoostersLots: '1.50',
    sumBonusLots: '30.00',
    sumBonusProfit: '5.00',
    instantWithdrawalAmount: '5.00',
    coefficient: '1.1',
    withdrawalAmount: '5.50',
    sumWithdrawalLots: '15.00',
    sumCurrentWithdrawalLots: '5.00',
    isProfitWithdrawn: false,
    registeredTs: 1704000000,
    endedTs: null,
    bannedTs: '2024-05-01T00:00:00Z',
    banReason: 'Inactivity',
    modifiedTs: 1714000000,
    balance: 30.0,
  },
]

export const $participants = restore<Participant[]>(
  getParticipants.doneData.map((a) => a.body.items),
  MOCK_PARTICIPANTS,
)

export const $totalCountOfParticipants = restore(
  getParticipants.doneData.map((a) => a.body.total),
  0,
)

sample({
  // @ts-ignore
  clock: participantsListMounted,
  fn: ({ limit, page, orderBy, orderDirection, filters }) => {
    if (orderDirection === 'descend') {
      orderDirection = '-'
    } else if (orderDirection === 'ascend') {
      orderDirection = ''
    }

    let query = {
      limit,
      page,
    }

    if (orderBy) {
      // @ts-ignore
      query['order'] = orderDirection + orderBy
    }

    // @ts-ignore
    query['populate'] = '["Bonus150Participant"]'

    if (filters) {
      const where: Record<string, any> = {}

      if (filters.clientId) where['client.id'] = filters.clientId
      if (filters.email) where['client.email'] = filters.email
      if (filters.tsname) where['client.tsname'] = filters.tsname
      if (filters.status && filters.status.length > 0)
        where['status'] = filters.status
      if (filters.withdrawalType && filters.withdrawalType.length > 0)
        where['withdrawalType'] = filters.withdrawalType

      if (Object.keys(where).length > 0) {
        // @ts-ignore
        query['where'] = JSON.stringify(where)
      }
    }

    return {
      query,
    }
  },
  target: getParticipants,
})

export const updateParticipantFx = fx<any, any>({
  method: 'PUT',
  url: '/bonus150/participant/',
})

export const banParticipantTriggered = createEvent<{
  id: number
  banReason: string
}>()

export const unbanParticipantTriggered = createEvent<{
  id: number
  banReason: string
}>()

sample({
  clock: banParticipantTriggered,
  fn: ({ id, banReason }) => ({
    url: `/bonus150/participant/${id}`,
    body: { banReason },
  }),
  target: updateParticipantFx,
})

sample({
  clock: unbanParticipantTriggered,
  fn: ({ id, banReason }) => ({
    url: `/bonus150/participant/${id}`,
    body: { banReason },
  }),
  target: updateParticipantFx,
})
